import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkContext } from '../../context/WorkContext';
import ProjectItem from '../WorkProjectsList/ProjectItem/ProjectItem';
import DescriptionSkeleton from './DescriptionSkeleton';
import ErrorState from '../ErrorState/ErrorState';
import { useCachedFetch } from './../../hooks/useCachedFetch';
import { AnimatedOnScroll } from '../AnimatedOnScroll';
import './Description.scss';

const Description = ({ page }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const { getRandomProject } = useWorkContext();
  const [randomProject1, setRandomProject1] = useState(null);
  const [randomProject2, setRandomProject2] = useState(null);
  
  const { data: descriptionJson, loading: dataLoading, error: dataError } = useCachedFetch('/data/locales/description.json');

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const descriptionData = useMemo(() => {
    return descriptionJson?.DescriptionData?.[page]?.[currentLang] || descriptionJson?.DescriptionData?.[page]?.en || null;
  }, [descriptionJson, page, currentLang]);

  const getUniqueRandomProjects = useCallback(() => {
    const project1 = getRandomProject();
    let project2 = getRandomProject();

    if (!project1 || !project2) {
      return { project1: null, project2: null };
    }

    while (project1.id === project2.id) {
      project2 = getRandomProject();
      if (!project2) break;
    }

    return { project1, project2 };
  }, [getRandomProject]);

  useEffect(() => {
    if (dataLoading) return;

    if (dataError) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    if (descriptionData?.items) {
      const { project1, project2 } = getUniqueRandomProjects();
      
      if (project1 && project2) {
        setRandomProject1(project1);
        setRandomProject2(project2);
        setHasError(false);
      } else {
        setHasError(true);
      }
    } else {
      setHasError(true);
    }

    setIsLoading(false);
  }, [dataLoading, dataError, descriptionData, getUniqueRandomProjects]);

  const handleRetry = useCallback(() => {
    setHasError(false);
    setIsLoading(true);
    
    setTimeout(() => {
      const { project1, project2 } = getUniqueRandomProjects();

      if (project1 && project2 && descriptionData?.items) {
        setRandomProject1(project1);
        setRandomProject2(project2);
        setHasError(false);
      } else {
        setHasError(true);
      }
      setIsLoading(false);
    }, 1000);
  }, [getUniqueRandomProjects, descriptionData]);

  if (isLoading) {
    return <DescriptionSkeleton />;
  }

  if (hasError || !descriptionData || !randomProject1 || !randomProject2) {
    return <ErrorState onRetry={handleRetry} />;
  }

  return (
    <div className="description__section">
      <div className="desc__wrapper">
        {descriptionData.items.slice(0, 2).map((item, index) => (
          <div key={index} className="desc__item">
            <AnimatedOnScroll animation="fade-up" delay={0}>
            <h3 className="desc__item__title">{item.title}</h3>
            </AnimatedOnScroll>
            <AnimatedOnScroll animation="fade-up" delay={100}>
            <p className="desc__item__text">{item.text}</p>
            </AnimatedOnScroll>
            <AnimatedOnScroll animation="fade-up" delay={200}>
            <p className="desc__item__text">{item.text2}</p>
            </AnimatedOnScroll>
          </div>
        ))}
      </div>
      <ProjectItem project={randomProject1} index={0} />
      <div className="desc__wrapper">
        {descriptionData.items.slice(2, 4).map((item, index) => (
          <div key={index} className="desc__item">
            <AnimatedOnScroll animation="fade-up" delay={0}>
            <h3 className="desc__item__title">{item.title}</h3>
            </AnimatedOnScroll>
            <AnimatedOnScroll animation="fade-up" delay={100}>
            <p className="desc__item__text">{item.text}</p>
            </AnimatedOnScroll>
            <AnimatedOnScroll animation="fade-up" delay={200}>
            <p className="desc__item__text">{item.text2}</p>
            </AnimatedOnScroll>
          </div>
        ))}
      </div>
      <ProjectItem project={randomProject2} index={1} />
    </div>
  );
};

export default React.memo(Description);